import { connect } from 'react-redux';
import { ARRAY_ERROR } from 'final-form';
import { FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect } from 'react';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { KNM_INFO } from 'CorePackage/MainModule/routes/routes';
import {
  HEAD_ORDER_NUMBER_BLOCK_ID,
  IMPOSSIBLE_CONDUCTION_REASON_BLOCK_ID,
  LEGAL_BASES_TITLE_ID,
  MONTH_BLOCK_ID,
  ORDER_DATE_BLOCK_ID,
  ORDER_NUMBER_BLOCK_ID,
  START_DATE_BLOCK_ID,
  STOP_DATE_BLOCK_ID,
  AUDITED_PERSON_AUTHORITY_TITLE_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { monthSelectOptions } from 'KnmPackage/MainModule/constants/monthSelectOptions';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmLegalBases } from 'KnmPackage/MainModule/components/KnmLegalBases';
import {
  getKnmDataMassDelete,
  getKnmFormRef,
  getKnmIsNew,
  getKnmStatusText,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IDatePickerProps } from 'UIPackage/MainModule/components/DatePicker';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';

import styles from './KnmInfo.module.scss';
import { IKnmInfoForm } from '../interfaces/IKnmInfoForm';
import { MassDeleteInfo } from '../../MainModule/components/MassDeleteInfo';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';

interface IKnmInfoProps {
  extensionOfKnmTimeChecked: IKnmInfoForm['extensionOfKnmTime'];
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnly?: boolean;
  onLegalBasesOpenButtonClick: () => void;
  federalLawCode?: string;
}

interface IKnmInfoStoreProps {
  status: string | undefined;
  isNew: boolean;
  formRef: IKnmFormRef | undefined;
  massDeleteData: IKnmMassDelete | null | undefined;
}

const KnmInfoWithoutStore = ({
  extensionOfKnmTimeChecked,
  typeCode,
  federalLawCode,
  watchOnly,
  onLegalBasesOpenButtonClick,
  status,
  isNew,
  formRef,
  massDeleteData,
}: IKnmInfoProps & IKnmInfoStoreProps) => {
  const isTypePP = typeCode === KnmTypeCodesEnum.PP;
  const isFederalLaw68 = federalLawCode === '68';
  const isWatchOnlyAuditedPersonAuthority = status !== 'В процессе формирования' && status !== 'Новое' && !isNew;

  useEffect(() => {
    if (!isFederalLaw68 || !federalLawCode) {
      formRef?.current.change('auditedPersonAuthority', undefined);
    }
  }, [isFederalLaw68, federalLawCode]);

  const handleStartDateFieldChange = useCallback(
    (value: IDatePickerProps['value']) => {
      const month =
        (parseDate({
          date: typeof value === 'string' ? value : '',
          parseFormat: DATE_FORMAT_LONG_FNS,
        })?.getMonth() as number) + 1;

      formRef?.current.change(
        'month',
        monthSelectOptions.find(option => option.value === month),
      );
      const filteredReasons = formRef?.current
        .getFieldState('reasons')
        ?.value?.map(item => ({ ...item, type: undefined }));

      formRef?.current.change('reasons', filteredReasons);
    },
    [formRef],
  );

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addAuditedPersonAuthority = useCallback(
    () => !watchOnly && push && push('auditedPersonAuthority', { listId: uuidv4() }),
    [push, watchOnly],
  );

  const makeRemoveAuditedPersonAuthorityHandler = (index: number) => () =>
    (!watchOnly || !isWatchOnlyAuditedPersonAuthority) && remove && remove('auditedPersonAuthority', index);

  return (
    <Section title="Общая информация" id={KNM_INFO}>
      <Row title="Дата и номер распоряжения или приказа руководителя органа контроля о проведении КНМ">
        <FieldBlock label="Номер приказа" id={ORDER_NUMBER_BLOCK_ID} size="half" color="black" placementLabel="left">
          <TextInputField className="shared-row-field" name="orderNumber" disabled={watchOnly} />
        </FieldBlock>
        <FieldBlock label="Дата приказа" id={ORDER_DATE_BLOCK_ID} size="half" color="black" placementLabel="left">
          <DatePickerField
            className="shared-row-field"
            name="orderDate"
            id="orderDate"
            disabled={watchOnly}
            minDate={new Date('01-01-2014')}
          />
        </FieldBlock>
      </Row>
      <Row>
        <CheckboxField
          className={styles.ExtensionOfKnmTimeField}
          labelClassName={styles.ExtensionOfKnmTimeFieldLabel}
          id="extensionOfKnmTime"
          name="extensionOfKnmTime"
          label="Приказ о продлении сроков проведения КНМ"
          size="medium"
          type="checkbox"
          theme="system"
          disabled={watchOnly}
        />
      </Row>

      {extensionOfKnmTimeChecked && (
        <Row title="Распоряжение или приказ руководителя органа контроля о продлении срока проведения КНМ">
          <FieldBlock
            label="Номер приказа"
            id={HEAD_ORDER_NUMBER_BLOCK_ID}
            size="half"
            color="black"
            placementLabel="left"
          >
            <TextInputField className="shared-row-field" name="headOrderNumber" disabled={watchOnly} />
          </FieldBlock>
          <FieldBlock label="Дата приказа" size="half" color="black" placementLabel="left">
            <DatePickerField
              className="shared-row-field"
              name="headOrderDate"
              disabled={watchOnly}
              minDate={new Date('01-01-2014')}
            />
          </FieldBlock>
        </Row>
      )}
      <Row>
        <FieldBlock label="Дата начала КНМ" id={START_DATE_BLOCK_ID} size="half" color="black" placementLabel="left">
          <DatePickerField
            className="shared-row-field"
            name="startDate"
            id="startDate"
            disabled={watchOnly}
            onChange={handleStartDateFieldChange}
          />
        </FieldBlock>
        <FieldBlock label="Дата окончания КНМ" id={STOP_DATE_BLOCK_ID} size="half" color="black" placementLabel="left">
          <DatePickerField
            className="shared-row-field"
            name="stopDate"
            id="stopDate"
            minDate={new Date('01-01-2014')}
            disabled={watchOnly}
          />
        </FieldBlock>
      </Row>

      {isTypePP && (
        <Row>
          <FieldBlock label="Месяц проведения КНМ" id={MONTH_BLOCK_ID} placementLabel="left" labelSize="col-4">
            <SelectInputField
              className="shared-row-field"
              name="month"
              options={monthSelectOptions}
              disabled={watchOnly}
              size="medium"
            />
          </FieldBlock>
        </Row>
      )}

      <MassDeleteInfo massDelete={massDeleteData} />

      <Row>
        <FieldBlock
          label="Правовые основания проведения КНМ"
          id={LEGAL_BASES_TITLE_ID}
          button={
            <Button
              className="shared-btn-add"
              type="button"
              variant="link"
              color="primary"
              text="Добавить"
              disabled={watchOnly}
              onClick={onLegalBasesOpenButtonClick}
            />
          }
        />
        <FormSpy subscription={{ errors: true, touched: true }}>
          {props => {
            const legalBasesFieldError = props.errors?.legalBases?.[ARRAY_ERROR];
            const isLegalBasesFieldTouched = props.touched?.legalBases;

            if (legalBasesFieldError && isLegalBasesFieldTouched) {
              return <p className="shared-input-error">{legalBasesFieldError}</p>;
            }

            return null;
          }}
        </FormSpy>
      </Row>
      <Row>
        <KnmLegalBases className={styles.LegalBasesList} fieldName="legalBases" />
      </Row>
      {isFederalLaw68 && (
        <>
          <Row>
            <FieldBlock
              label="Полномочия проверяемого лица, реализация которых подлежит проверке"
              id={AUDITED_PERSON_AUTHORITY_TITLE_BLOCK_ID}
              button={
                <Button
                  className="shared-btn-add"
                  type="button"
                  variant="link"
                  color="primary"
                  text="Добавить"
                  disabled={watchOnly || isWatchOnlyAuditedPersonAuthority}
                  onClick={addAuditedPersonAuthority}
                />
              }
            />
            <FormSpy subscription={{ errors: true, touched: true }}>
              {props => {
                const auditedPersonAuthorityFieldError = props.errors?.auditedPersonAuthority?.[ARRAY_ERROR];
                const isAuditedPersonAuthorityFieldTouched = props.touched?.auditedPersonAuthority;

                if (auditedPersonAuthorityFieldError && isAuditedPersonAuthorityFieldTouched) {
                  return <p className="shared-input-error">{auditedPersonAuthorityFieldError}</p>;
                }

                return null;
              }}
            </FormSpy>
          </Row>
          <Row>
            <FieldArray name="auditedPersonAuthority">
              {({ fields }) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((name, index) => (
                  <Row key={fields.value[index].listId} notWrap>
                    <FieldBlock>
                      <TextareaField
                        name={`${name}.authority`}
                        disabled={watchOnly || isWatchOnlyAuditedPersonAuthority}
                        placeholder="Введите полномочие"
                      />
                    </FieldBlock>
                    <CloseButton
                      className="shared-deleting-row-btn"
                      onClick={makeRemoveAuditedPersonAuthorityHandler(index)}
                      disabled={watchOnly || isWatchOnlyAuditedPersonAuthority}
                    />
                  </Row>
                ));
              }}
            </FieldArray>
          </Row>
        </>
      )}

      <Row>
        <FieldBlock
          label="Сведения о причинах невозможности проведения КНМ (заполняется при наличии сведений)"
          id={IMPOSSIBLE_CONDUCTION_REASON_BLOCK_ID}
        >
          <TextareaField
            id="impossibleConductionReason"
            name="impossibleConductionReason"
            disabled={watchOnly}
            maxLength={1024}
          />
        </FieldBlock>
      </Row>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmInfoStoreProps => ({
  isNew: getKnmIsNew(state),
  status: getKnmStatusText(state),
  formRef: getKnmFormRef(state),
  massDeleteData: getKnmDataMassDelete(state),
});

export const KnmInfo = connect(mapStateToProps)(KnmInfoWithoutStore);
